'use client';

import { useId } from 'react';
import { FaLinkedin } from 'react-icons/fa6';

import styles from './audience-engine-diagram.module.css';
import { audienceDiagram, getDiagramStageState } from './use-case-diagram-data';

export const audienceOutcomes = [
  { icon: 'people', text: 'An audience of relevant buyers' },
  { icon: 'bulb', text: 'Buyer-informed content' },
  { icon: 'bars', text: 'Pipeline from engaged buyers' }
] as const;

function People({ fill }: { fill: string }) {
  return (
    <g fill={fill}>
      <circle
        cx="90"
        cy="82"
        r="15"
      />
      <circle
        cx="54"
        cy="94"
        r="11"
      />
      <circle
        cx="126"
        cy="94"
        r="11"
      />
      <path d="M65 139v-16c0-15 11-23 25-23s25 8 25 23v16H65ZM35 139v-14c0-12 8-18 19-18 5 0 9 1 12 4-5 6-7 12-7 21v7H35Zm86 0v-7c0-9-2-15-7-21 3-3 7-4 12-4 11 0 19 6 19 18v14h-24Z" />
    </g>
  );
}

function Bars({ fill }: { fill: string }) {
  return (
    <g fill={fill}>
      <rect
        x="47"
        y="99"
        width="23"
        height="40"
        rx="5"
      />
      <rect
        x="79"
        y="70"
        width="23"
        height="69"
        rx="5"
      />
      <rect
        x="111"
        y="39"
        width="23"
        height="100"
        rx="5"
      />
    </g>
  );
}

/** Layered, native-vector illustrations retain the supplied image's visual detail. */
function AudienceIllustration({ stage }: { stage: number }) {
  const id = `audience-scene-${useId().replace(/:/g, '')}`;
  const green = `url(#${id}-green)`;
  return (
    <svg
      className={styles.illustration}
      viewBox="0 0 190 180"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={`${id}-green`}
          x1="0"
          y1="0"
          x2="0.65"
          y2="1"
        >
          <stop stopColor="#38bd86" />
          <stop
            offset="0.55"
            stopColor="#159260"
          />
          <stop
            offset="1"
            stopColor="#075a3d"
          />
        </linearGradient>
        <radialGradient
          id={`${id}-disc`}
          cx="0.28"
          cy="0.2"
          r="0.85"
        >
          <stop stopColor="#eafaf2" />
          <stop
            offset="1"
            stopColor="#d2f1e4"
          />
        </radialGradient>
      </defs>
      <circle
        cx="90"
        cy="90"
        r="80"
        fill={`url(#${id}-disc)`}
      />
      {stage === 1 && (
        <>
          <People fill={green} />
          <rect
            x="130"
            y="39"
            width="43"
            height="45"
            rx="8"
            fill="white"
          />
          <FaLinkedin
            x="130"
            y="36"
            size={44}
            color="#0a66c2"
          />
          <circle
            cx="173"
            cy="87"
            r="19"
            fill="#fff"
            stroke="#e6f3ed"
          />
          <path
            d="M173 79v16m-8-8h16"
            stroke="#128357"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </>
      )}
      {stage === 2 && (
        <>
          <path
            d="M42 47h49a16 16 0 0 1 16 16v29a16 16 0 0 1-16 16H62l-17 15v-16a16 16 0 0 1-17-16V64a17 17 0 0 1 14-17Z"
            fill={green}
          />
          <path
            d="M49 68h38M49 86h28"
            stroke="#edfff7"
            strokeWidth="7"
            strokeLinecap="round"
          />
          <path
            d="M105 96h28a27 27 0 0 1 27 27c0 11-6 20-16 25v15l-17-13h-22a27 27 0 0 1 0-54Z"
            fill="white"
            stroke="#e1f1e9"
          />
          {[105, 120, 135].map((x) => (
            <circle
              key={x}
              cx={x}
              cy="123"
              r="4.5"
              fill="#0f8355"
            />
          ))}
        </>
      )}
      {stage === 3 && (
        <>
          <rect
            x="39"
            y="38"
            width="84"
            height="103"
            rx="15"
            fill="#fff"
            stroke="#86c8ad"
            strokeWidth="3"
          />
          <path
            d="M58 63h42"
            stroke="#128758"
            strokeWidth="9"
            strokeLinecap="round"
          />
          <path
            d="M58 83h29m-29 16h38m-38 16h22"
            stroke="#8fc6b2"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path
            d="M132 96h8a28 28 0 0 1 28 28c0 16-13 28-28 28h-12l-16 12v-18a27 27 0 0 1-8-21 29 29 0 0 1 28-29Z"
            fill={green}
          />
          {[121, 136, 151].map((x) => (
            <circle
              key={x}
              cx={x}
              cy="124"
              r="4"
              fill="#f3fff8"
            />
          ))}
        </>
      )}
      {stage === 4 && (
        <>
          <Bars fill={green} />
          <circle
            cx="134"
            cy="126"
            r="31"
            fill="white"
          />
          <circle
            cx="134"
            cy="126"
            r="26"
            fill={green}
          />
          <circle
            cx="134"
            cy="118"
            r="8"
            fill="white"
          />
          <path
            d="M120 142v-5c0-7 6-11 14-11s14 4 14 11v5h-28Z"
            fill="white"
          />
        </>
      )}
      {stage === 0 && (
        <>
          <rect
            x="35"
            y="35"
            width="107"
            height="113"
            rx="14"
            fill="#fff"
            stroke="#91cdb4"
            strokeWidth="2.5"
          />
          <g fill={green}>
            <path d="M49 70V47l20-6v29H49Z" />
            <circle
              cx="57"
              cy="90"
              r="7"
            />
            <path d="M47 105v-4c0-6 4-9 10-9s10 3 10 9v4H47Z" />
            <circle
              cx="57"
              cy="120"
              r="7"
            />
            <path d="M47 135v-4c0-6 4-9 10-9s10 3 10 9v4H47Z" />
          </g>
          <path
            d="M54 51v3m9-6v3m-9 8v3m9-6v3"
            stroke="#f2fff8"
            strokeWidth="3"
          />
          <path
            d="M80 54h43m-43 11h29"
            stroke="#159260"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <path
            d="M80 90h39m-39 12h25m-25 18h31m-31 12h21"
            stroke="#b5cbc1"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <path
            d="m153 136 20 21"
            stroke="#0f7952"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <circle
            cx="132"
            cy="115"
            r="30"
            fill="#f3fff9"
            stroke="#169764"
            strokeWidth="7"
          />
          <circle
            cx="132"
            cy="107"
            r="8"
            fill={green}
          />
          <path
            d="M118 133v-5c0-8 6-12 14-12s14 4 14 12v5h-28Z"
            fill={green}
          />
        </>
      )}
    </svg>
  );
}

function OutcomeIcon({
  kind
}: {
  kind: (typeof audienceOutcomes)[number]['icon'];
}) {
  return (
    <svg
      className={styles.outcomeIcon}
      viewBox="0 0 80 80"
      aria-hidden="true"
    >
      <circle
        cx="40"
        cy="40"
        r="39"
        fill="#def5ea"
      />
      {kind === 'people' && (
        <g transform="translate(2 0) scale(.42)">
          <People fill="#12835a" />
        </g>
      )}
      {kind === 'bars' && (
        <g transform="translate(1 0) scale(.43)">
          <Bars fill="#12835a" />
        </g>
      )}
      {kind === 'bulb' && (
        <g fill="#12835a">
          <path d="M29 53c0-5-1-7-4-11a18 18 0 1 1 30 0c-3 4-4 6-4 11H29Z" />
          <rect
            x="30"
            y="57"
            width="20"
            height="5"
            rx="2"
          />
          <rect
            x="34"
            y="65"
            width="12"
            height="5"
            rx="2"
          />
          <path
            d="M40 9V4M19 17l-4-4m50 0-4 4M13 34H7m60 0h6"
            stroke="#12835a"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </g>
      )}
    </svg>
  );
}

/** Real text wraps within each card; the illustrations and connectors stay vector-sharp. */
export function AudienceEngineDiagram({
  focus = -1,
  progressive = false
}: {
  focus?: number;
  progressive?: boolean;
}) {
  const count = audienceDiagram.stages.length;
  const complete = focus === count;
  const loopVisible = focus === -1 || focus >= count - 1;
  return (
    <div
      className={styles.diagram}
      data-native-audience-diagram
      data-progressive={progressive}
    >
      <ol className={styles.stages}>
        {audienceDiagram.stages.map((stage, index) => (
          <li
            key={stage.title}
            className={styles.stage}
            data-audience-stage={index}
            data-active={focus === index}
            data-dimmed={focus >= 0 && focus !== index}
            data-complete={complete}
            data-disclosure-state={getDiagramStageState(focus, index, count)}
          >
            <span
              className={styles.badge}
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <AudienceIllustration stage={index} />
            <h3>{stage.title}</h3>
            <p>{stage.description}</p>
            {index < count - 1 && (
              <svg
                className={styles.connector}
                data-revealed={focus < 0 || focus > index}
                viewBox="0 0 36 34"
                aria-hidden="true"
              >
                <path
                  d="M4 17h27m-10-11 11 11-11 11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </li>
        ))}
      </ol>
      <div
        className={styles.returnLoop}
        data-highlighted={loopVisible}
        data-revealed={focus < 0 || complete}
      >
        <svg
          viewBox="0 565 1672 110"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <path
            d={audienceDiagram.returnPath}
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p>
          <span aria-hidden="true">↻</span> {audienceDiagram.returnCopy}
        </p>
      </div>
      <div
        className={styles.outcomes}
        data-dimmed={focus >= 0 && !complete}
        data-active={complete}
        data-revealed={focus < 0 || complete}
      >
        <p className={styles.outcomeLabel}>Outcomes</p>
        <ul>
          {audienceOutcomes.map((outcome) => (
            <li key={outcome.text}>
              <OutcomeIcon kind={outcome.icon} />
              <span>{outcome.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
